/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import { getCartState } from '@msdyn365-commerce/global-state';
import { CartLine, CommerceProperty, FeatureState, QueryResultSettings } from '@msdyn365-commerce/retail-proxy';
import { addCartLinesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';
import { IOrderTemplateLinesState, OrderTemplateLine } from '../../../themes/adventureworks/views/common/order-templates';
import { getPaginatedOrderTemplateLinesAction, GetPaginatedOrderTemplateLinesInput } from '@msdyn365-commerce-modules/retail-actions';

/**
 *  Input class for the AddOrderTemplateToCart data action.
 */
export class AddOrderTemplateToCartInput implements IActionInput {
    public readonly orderTemplateId: string;
    public readonly orderTemplateLines?:OrderTemplateLine[];
    public readonly skipProductIds?: string[];
    public readonly featureStates?: FeatureState[];
    constructor(orderTemplateId: string,orderTemplateLines?:OrderTemplateLine[],skipProductIds?: string[]) {
        this.orderTemplateId = orderTemplateId;
        this.orderTemplateLines = orderTemplateLines;
        this.skipProductIds = skipProductIds;
    }

    public getCacheKey = () => 'AddOrderTemplateToCartInput';

    public getCacheObjectType = () => 'AddOrderTemplateToCartInput';

    public dataCacheType = (): CacheType => 'none';
}

/**
 * CreateInput method for the AddOrderTemplateToCart method.
 * @param {ICreateActionContext} inputData The input data passed to the createInput method.
 * @param {string} orderTemplateId The id of template.
 */
export const addOrderTemplateToCartInput = (inputData: ICreateActionContext, orderTemplateId: string): AddOrderTemplateToCartInput => {
    const { requestContext } = inputData;
    if (!requestContext.user.isAuthenticated) {
        throw new Error('Unable to create order template. User is not authenticated.');
    }
    return new AddOrderTemplateToCartInput(orderTemplateId);
};

/**
 * The action method for the AddOrderTemplateToCart data action.
 * @param {AddOrderTemplateToCartInput} input The action input.
 * @param {IActionContext} ctx The action context.
 * @param context
 */
export async function addOrderTemplateToCartAction(input: AddOrderTemplateToCartInput, context: IActionContext): Promise<CartLine[]> {
    const cartState = await getCartState(context);
    const chunkCount = 1000;
    const queryResultSettings: QueryResultSettings = {
        Paging: {
            Top: chunkCount,
            Skip: 0
        }
    };
    const getPaginatedOrderTemplateLinesInput = new GetPaginatedOrderTemplateLinesInput(input.orderTemplateId, queryResultSettings);
    let orderTemplateLines = await getPaginatedOrderTemplateLinesAction(getPaginatedOrderTemplateLinesInput, context) as IOrderTemplateLinesState;
    let addedCartLines: CartLine[] = [];
    orderTemplateLines.lines = orderTemplateLines.lines.filter(function(item,idx){
        var isActive = false;
        let isPriceAvailable = false;

        if(item.fullProduct && item.fullProduct.ProductDetails && item.fullProduct.ProductDetails.ExtensionProperties)
        {
            const isActiveProps = item.fullProduct.ProductDetails.ExtensionProperties.filter(x=> x.Key === 'IsActive');
            if(!isActive && isActiveProps && isActiveProps[0] && isActiveProps[0].Value !== undefined)
            {
                isActive= isActiveProps[0].Value.BooleanValue !== undefined ? isActiveProps[0].Value.BooleanValue : false;
            }
        }
        if(item.fullProduct && item.fullProduct.TradeAgreement)
            {
                if(item.fullProduct && item.fullProduct.TradeAgreement && item.fullProduct.TradeAgreement.TradeAgreementPrice !== 0)
                {
                    isPriceAvailable = true;
                }
            }
        if(isActive && isPriceAvailable)
        {return item;}
        else
        {return false;}
    });
    orderTemplateLines.totalCount = orderTemplateLines.lines.length;
    for (let skip = chunkCount; orderTemplateLines.lines.length > 0; skip += chunkCount) {
        const cartLines: CartLine[] = orderTemplateLines.lines.filter(function(prodLine) {
            if(input.skipProductIds && input.skipProductIds.length > 0)
            {
                const itemId = prodLine.fullProduct && prodLine.fullProduct.ProductDetails && prodLine.fullProduct.ProductDetails.ItemId ? prodLine.fullProduct.ProductDetails.ItemId : '';
                if(input.skipProductIds.find(x => x === itemId) )
                {
                    return false;
                }
                else
                {
                    return true;
                }
            }
            else
            {return true;}
        }).map(productLine => {
                let cartLine: CartLine ={
                    ItemId: productLine.fullProduct?.ProductDetails.ItemId,
                    ProductId: productLine.productListLine.ProductId,
                    Quantity: productLine.productListLine.Quantity,
                    TrackingId: '',
                    UnitOfMeasureSymbol: productLine.productListLine.UnitOfMeasure
                };
                const prodline = input.orderTemplateLines?.filter(x => x.fullProduct?.ProductDetails.ItemId === productLine.fullProduct?.ProductDetails.ItemId)[0];
                if(prodline)
                {
                    if(prodline.fullProduct?.AlternateProduct && prodline.fullProduct?.AlternateProduct.serviceResp && prodline.fullProduct?.AlternateProduct.serviceResp[0].UseAltItemId === 'Always' && prodline.fullProduct?.AlternateProduct.currentProd)
                    {
                        cartLine = {
                            ItemId:  prodline.fullProduct?.AlternateProduct.currentProd.productDtl!.ItemId,
                            ProductId:  prodline.fullProduct?.AlternateProduct.currentProd.productDtl!.RecordId,
                            Quantity: productLine.productListLine.Quantity,
                            TrackingId: '',
                            UnitOfMeasureSymbol:  prodline.fullProduct?.AlternateProduct.currentProd.productDtl!.DefaultUnitOfMeasure
                        };

                    }
                }
                return cartLine;

        });
       var updatedCart = await addCartLinesAsync({ callerContext: context }, cartState.cart.Id, cartLines, cartState.cart.Version).catch(error => {
            context.telemetry.trace(error);
            context.telemetry.trace('Unable to add order template to cart.');
            throw error;
        });
        if(updatedCart && updatedCart.CartLines)
        {
            for(var i=0; i<orderTemplateLines.lines.length; i++){
                var line = orderTemplateLines.lines[i];
                const cartState = await getCartState(context);
                const prodline = input.orderTemplateLines?.filter(x => x.fullProduct?.ProductDetails.ItemId === line.fullProduct?.ProductDetails.ItemId)[0];
                if(prodline && (prodline.fullProduct?.IsOversizedItem || prodline.fullProduct?.IsAlternateOversized))
                {
                    const cartLines = updatedCart.CartLines.filter(x => x.ItemId === prodline.fullProduct?.ProductDetails.ItemId || x.ItemId === prodline.fullProduct?.AlternateProduct?.currentProd?.productDtl?.ItemId);
                    if(cartLines && cartLines.length>0)
                    {
                        const extProp: CommerceProperty[] = [];
                        const existingExtensionProp = cartState && cartState.cart && cartState.cart.ExtensionProperties;
                        cartLines.forEach(cartLine => {
                            const existingProp :CommerceProperty | undefined = existingExtensionProp && existingExtensionProp.length > 0 ? existingExtensionProp.find(ext=>ext.Key === cartLine.LineId) : undefined;
                            if(existingProp === undefined){
                                const updatedExistingProp :CommerceProperty = {Key : cartLine.LineId, Value:{StringValue: 'Oversized Item'}};
                                extProp.push(updatedExistingProp);
                            }
                        });
                        cartState.updateExtensionProperties({newExtensionProperties: extProp});
                    }
                }
            }
        }
        addedCartLines = addedCartLines.concat(cartLines);
        getPaginatedOrderTemplateLinesInput.queryResultSettings.Paging!.Skip = skip;
        orderTemplateLines = await getPaginatedOrderTemplateLinesAction(getPaginatedOrderTemplateLinesInput, context) as IOrderTemplateLinesState;
    }
    await cartState.refreshCart({});
    return addedCartLines;
}

/**
 * The AddOrderTemplateToCart Data Action.
 * Returns created order template.
 */
export const addOrderTemplateToCartActionDataAction = createObservableDataAction<CartLine[]>({
    id: '@msdyn365-commerce-modules/retail-actions/order-templates/add-order-template-to-cart',
    action: <IAction<CartLine[]>>addOrderTemplateToCartAction,
    input: addOrderTemplateToCartInput
});
export default addOrderTemplateToCartActionDataAction;
